import React, { memo, useCallback, useRef } from 'react'
import { FormHelperText, FormLabel, Grid, TextField } from '@mui/material'
import { Controller, useFormContext, get } from 'react-hook-form'
import { NumericFormat } from 'react-number-format'

import useStyles from './styles'

const defaultSx = {
  '& .MuiInputBase-input': {
    fontSize: '14px',
    textAlign: 'right',
    padding: 0,
  },
  '& .MuiOutlinedInput-root': {
    padding: '8px 10px',
    height: '40px',
  },
}

const InputComponent = memo(function InputComponent({ field, onBlur, onFocus, ...props }) {
  const hasBlurredRef = useRef(false)

  const handleFocus = useCallback(
    event => {
      hasBlurredRef.current = false
      if (onFocus) onFocus(event)
    },
    [onFocus]
  )

  const handleBlur = useCallback(
    event => {
      if (hasBlurredRef.current) return

      hasBlurredRef.current = true
      if (field?.onBlur) field.onBlur(event)
      if (onBlur) onBlur(event)
    },
    [field, onBlur]
  )

  return (
    <NumericFormat
      customInput={TextField}
      variant="outlined"
      fullWidth
      decimalSeparator=","
      thousandSeparator="."
      decimalScale={2}
      fixedDecimalScale
      allowNegative={props.allowNegative ?? false}
      allowEmptyFormatting
      value={field?.value ?? ''}
      isAllowed={props.isAllowed ?? undefined}
      onValueChange={values => {
        if (field?.onChange) {
          field.onChange(values.floatValue)
        }
      }}
      onBlur={handleBlur}
      onFocus={handleFocus}
      name={field?.name}
      placeholder="0,00"
      sx={defaultSx}
      inputProps={{
        'data-field-path': field?.name,
      }}
      {...props}
    />
  )
})

export const NumericInput = memo(function NumericInput({
  name,
  label,
  required,
  rules,
  hasError = false,
  variant = 'outlined',
  defaultValue = 0,
  labelClasses,
  showErrorsOnlyAfterSubmit = false,
  ...props
}) {
  const styles = useStyles()
  const {
    control,
    formState: { errors, isSubmitted },
  } = useFormContext()

  const error = get(errors, name)
  const canShowError = !showErrorsOnlyAfterSubmit || isSubmitted
  const displayError = canShowError ? error : undefined
  const hasErrorOnField = !!displayError || (!!hasError && canShowError)

  return (
    <Grid className={styles.container}>
      {label && (
        <FormLabel
          id={name}
          className={`${styles.label} ${labelClasses ?? ''}`}
          error={hasErrorOnField}
          disabled={props.disabled}
        >
          {label}
          {required && <span className={styles.required}>*</span>}
        </FormLabel>
      )}

      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        rules={rules}
        render={field => (
          <InputComponent field={field} variant={variant} error={hasErrorOnField} {...props} />
        )}
      />

      <FormHelperText className={styles.errorMessage} error={hasErrorOnField}>
        {displayError?.message}
      </FormHelperText>
    </Grid>
  )
})
